/**
 * Stored video time — what a host has on Cloudflare Stream, measured in the
 * unit Stream bills in (minutes), from `GET /media-library/storage` →
 * `storage.videoMinutes`.
 *
 * One helper for every meter (sidebar, Media Library, Dashboard) so the three
 * can't show three different numbers for the same allowance, which is how the
 * sidebar and the Media Library drifted over the GB figure before.
 *
 * Returns null when there is nothing honest to show: an older API without the
 * field, or a usage the server couldn't measure (`used: null`). A meter reading
 * "0 min" in that case would tell a host they have room they may not have.
 */

export function formatVideoMinutes(minutes) {
  const m = Math.max(0, Number(minutes) || 0);
  if (m < 60) return `${Math.round(m)} min`;
  const hrs = Math.round((m / 60) * 10) / 10;
  return `${hrs} hrs`;
}

export function videoMinutesDisplay(storage) {
  const vm = storage?.videoMinutes;
  if (!vm || vm.used === null || vm.used === undefined) return null;

  const used = Number(vm.used) || 0;
  const unlimited = vm.limit === -1 || vm.limit === null || vm.limit === undefined;
  const limit = unlimited ? null : Number(vm.limit);
  const percent = unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));

  let tone = 'ok';
  if (!unlimited) {
    if (percent >= 90) tone = 'critical';
    else if (percent >= 70) tone = 'warning';
  }

  return {
    used,
    limit,
    unlimited,
    percent,
    tone,
    usedLabel: formatVideoMinutes(used),
    limitLabel: unlimited ? 'Unlimited' : formatVideoMinutes(limit),
    atLimit: !unlimited && used >= limit,
    // Strictly over: this is when older recordings start being removed.
    over: !unlimited && used > limit,
    // How old a recording must be before the over-cap pass removes it; null
    // when the server didn't say (older API), so no number is invented.
    overCapRetentionDays: Number.isInteger(vm.overCapRetentionDays) && vm.overCapRetentionDays > 0
      ? vm.overCapRetentionDays
      : null,
  };
}

/** The bar colour the Media Library's storage bar already uses per tone. */
export const VIDEO_MINUTES_TONE_COLOR = {
  ok: '#22c55e',
  warning: '#f59e0b',
  critical: '#ef4444',
};
